import { ReactNode, useCallback, useEffect } from 'react';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { InstantCardSearch } from '@sorare/core/src/components/search/InstantSearch';
import {
  AnalyticTag,
  Props as InstantCardSearchProps,
} from '@sorare/core/src/components/search/InstantSearch/types';
import SearchCardsContextProvider from '@sorare/core/src/contexts/searchCards/Provider';
import useVirtualToggle from '@sorare/core/src/hooks/useVirtualToggle';
import { VIRTUAL_TOGGLE_FILTERS } from '@sorare/core/src/lib/filters';

import { useCurrentMarketplaceUserContext } from 'contexts/currentMarketplaceUser';
import CurrentMarketplaceUserProvider from 'contexts/currentMarketplaceUser/Provider';

import SearchLayout from './SearchLayout';
import { Props } from './types';

export interface FullProps extends Props {
  sport: Sport;
  commonMarket?: boolean;
  defaultFilters?: string[];
  getOptionalFilters?: InstantCardSearchProps['getOptionalFilters'];
  analyticsTags: AnalyticTag[];
  distinct?: InstantCardSearchProps['distinct'];
  defaultHitsPerPage?: number;
  editableLists?: boolean;
  alwaysShowFavoriteButton?: boolean;
  searchClient?: InstantCardSearchProps['searchClient'];
  onFavoriteFilterChange?: (v: boolean) => void;
}

const FavoriteFilterChangeNotifier = ({
  onChange,
}: {
  onChange: (v: boolean) => void;
}) => {
  const { currentRefinement } = useVirtualToggle<boolean>({
    name: VIRTUAL_TOGGLE_FILTERS.favoriteFilter.name,
  });

  useEffect(() => {
    onChange(currentRefinement);
  }, [currentRefinement, onChange]);

  return null;
};

const AdvancedCardSearch = ({
  children,
  sport,
  commonMarket,
  analyticsTags,
  defaultFilters,
  getOptionalFilters,
  defaultSort,
  initialIndexUIState,
  cardFilters,
  advancedCardFilters,
  defaultHitsPerPage,
  editableLists,
  distinct,
  searchClient,
  onFavoriteFilterChange,
  ...rest
}: FullProps) => {
  const index = defaultSort || rest.sorts[0];
  const currentMarketplaceUserContext = useCurrentMarketplaceUserContext();

  const Wrapper = useCallback(
    ({ children: c }: { children: ReactNode }) => (
      <CurrentMarketplaceUserProvider sport={sport}>
        {c}
      </CurrentMarketplaceUserProvider>
    ),
    [sport]
  );

  return (
    <ConditionalWrapper wrap={!currentMarketplaceUserContext} Wrapper={Wrapper}>
      <SearchCardsContextProvider
        commonMarket={Boolean(commonMarket)}
        cardFilters={cardFilters}
        advancedCardFilters={advancedCardFilters}
        editableLists={editableLists}
        galleryOwnerSlug={rest.galleryOwnerSlug}
        legacyFavoriteFilter={!onFavoriteFilterChange}
      >
        <InstantCardSearch
          {...(index && { indexes: [index] })}
          availableSorts={rest.sorts}
          analyticsTags={analyticsTags}
          sport={sport}
          defaultFilters={defaultFilters}
          getOptionalFilters={getOptionalFilters}
          urlState
          initialIndexUIState={initialIndexUIState}
          searchClient={searchClient}
          {...(defaultHitsPerPage && { defaultHitsPerPage })}
          {...(distinct && { distinct })}
          {...(rest.attributesToRetrieve && {
            attributesToRetrieve: rest.attributesToRetrieve,
          })}
        >
          {onFavoriteFilterChange && (
            <FavoriteFilterChangeNotifier onChange={onFavoriteFilterChange} />
          )}
          <SearchLayout
            cardFilters={cardFilters}
            advancedCardFilters={advancedCardFilters}
            editableLists={editableLists}
            sport={sport}
            {...rest}
          >
            {children}
          </SearchLayout>
        </InstantCardSearch>
      </SearchCardsContextProvider>
    </ConditionalWrapper>
  );
};

export default AdvancedCardSearch;
